import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  UploadCloud, Send, Plus, ChevronDown, Eye, Download, MoreVertical, Check, X, Trash2,
  ShieldCheck, FolderOpen, FileText, Truck, PackageCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { DOC_SECTIONS, STATUS_CONFIG, docStatus, sourceLabel, fmtDate } from './docCatalog';
import PreviewDocumentModal from './PreviewDocumentModal';
import LoadingNotesSection from '../LoadingNotesSection';
import LoadingNoteModal from '../LoadingNoteModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KPI = ({ label, value, cls }) => (
  <div className="flex-1 min-w-[120px] border rounded-xl px-4 py-3 bg-white">
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${cls}`}>{value}</p>
  </div>
);

const GLOBAL = {
  conforme: { label: 'Conforme', cls: 'text-emerald-600', icon: ShieldCheck },
  non_conforme: { label: 'Non conforme', cls: 'text-red-600', icon: X },
  a_verifier: { label: 'À vérifier', cls: 'text-amber-600', icon: Eye },
};

const DocRow = ({ item, versions, onAdd, onPreview, onValidate, onDelete, onRequestOne, highlight, canValidate = true }) => {
  const st = docStatus(versions);
  const has = versions.length > 0;
  const cfg = STATUS_CONFIG[st];
  const latest = versions[0];
  const Icon = item.icon || FileText;
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 flex-wrap hover:bg-gray-50/70 transition-colors ${highlight && st === 'manquant' ? 'bg-red-50/40' : ''}`}
      data-testid={`doc-row-${item.type}`}>
      <div className="flex items-center gap-3 min-w-[200px] flex-1">
        <div className="w-8 h-8 rounded-lg bg-gray-50 border flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-500" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{item.label}</span>
            <Badge variant="outline" className={`text-[9px] px-1.5 ${item.required ? 'border-gray-300 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
              {item.required ? 'Obligatoire' : 'Optionnel'}
            </Badge>
          </div>
        </div>
      </div>
      <div className="w-24"><Badge variant="outline" className={`text-[10px] ${cfg.cls}`}>{cfg.label}</Badge></div>
      <div className="w-20 text-xs text-gray-500">{has ? sourceLabel(latest) : '—'}</div>
      <div className="w-24 text-xs text-gray-500">{has ? fmtDate(latest.uploadedAt) : '—'}</div>
      <div className="flex items-center gap-1.5 ml-auto">
        {has ? (
          <Button size="sm" variant="outline" className="h-8 text-gray-600" onClick={() => onPreview(item, versions)} data-testid={`preview-${item.type}`}>
            <Eye className="w-4 h-4 mr-1" /> Voir
          </Button>
        ) : (
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => onAdd(item.type)} data-testid={`add-${item.type}`}>
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid={`menu-${item.type}`}><ChevronDown className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {has && <DropdownMenuItem onClick={() => onPreview(item, versions)}><Eye className="w-4 h-4 mr-2" /> Prévisualiser</DropdownMenuItem>}
            {has && <DropdownMenuItem asChild><a href={`${BACKEND_URL}${latest.filePath}`} download target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4 mr-2" /> Télécharger</a></DropdownMenuItem>}
            <DropdownMenuItem onClick={() => onAdd(item.type)}><UploadCloud className="w-4 h-4 mr-2" /> {has ? 'Remplacer' : 'Ajouter'}</DropdownMenuItem>
            {has && st !== 'valide' && canValidate && <DropdownMenuItem onClick={() => onValidate(latest.id, 'validated')} className="text-emerald-600"><Check className="w-4 h-4 mr-2" /> Valider</DropdownMenuItem>}
            {has && st !== 'refuse' && canValidate && <DropdownMenuItem onClick={() => onValidate(latest.id, 'rejected')} className="text-red-600"><X className="w-4 h-4 mr-2" /> Refuser</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => onRequestOne(item.label)}><Send className="w-4 h-4 mr-2" /> Demander au terrain</DropdownMenuItem>
            {has && <DropdownMenuItem onClick={() => onDelete(latest.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Supprimer</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const Section = ({ section, docsByType, count, total, ...rowProps }) => {
  const [open, setOpen] = useState(true);
  const SIcon = section.icon;
  return (
    <Card data-testid={`doc-section-${section.key}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              <SIcon className={`w-5 h-5 ${section.color}`} />
              <span className="text-sm font-semibold text-gray-800">{section.title}</span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-[11px] text-blue-700 border-blue-200 bg-blue-50">{count} / {total} reçus</Badge>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t divide-y">
            <div className="flex items-center gap-3 px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400">
              <span className="flex-1 min-w-[200px]">Document</span>
              <span className="w-24">Statut</span>
              <span className="w-20">Source</span>
              <span className="w-24">Mise à jour</span>
              <span className="ml-auto">Actions</span>
            </div>
            {section.docs.map((d) => (
              <DocRow key={d.type} item={d} versions={docsByType(d.type)} {...rowProps} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const DocumentsComplianceCenter = ({ operationId, operation, docData, onAddDoc, onRequestField, sectionDContent, hideSectionD = false, canValidate = true }) => {
  const { documents, docsByType, signItems, stats, refetch } = docData;
  const [preview, setPreview] = useState(null); // { item, versions }
  const [highlight, setHighlight] = useState(false);
  const [noteModal, setNoteModal] = useState({ open: false, type: null });

  const g = GLOBAL[stats.globalStatus] || GLOBAL.non_conforme;

  const quickValidate = async (docId, status) => {
    try {
      await axios.put(`${API}/operations/${operationId}/documents/${docId}/validate`,
        { status, reason: status === 'rejected' ? 'Refusé' : undefined });
      toast.success(status === 'validated' ? 'Document validé' : 'Document refusé');
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
  };

  const del = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API}/operations/${operationId}/documents/${docId}`);
      toast.success('Document supprimé'); refetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };

  React.useEffect(() => {
    const handler = () => { setHighlight(true); setTimeout(() => setHighlight(false), 2500); };
    window.addEventListener('op-docs-show-missing', handler);
    return () => window.removeEventListener('op-docs-show-missing', handler);
  }, []);

  const sectionCount = (section) => section.docs.filter((d) => docsByType(d.type).length > 0).length;
  const recentDocs = [...documents].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)).slice(0, 8);
  const dCount = signItems.filter((s) => !!s.note).length;

  const rowProps = {
    onAdd: (type) => onAddDoc(type),
    onPreview: (item, versions) => setPreview({ item, versions }),
    onValidate: quickValidate,
    onDelete: del,
    onRequestOne: (label) => onRequestField([label]),
    highlight,
    canValidate,
  };

  return (
    <div className="space-y-5" data-testid="documents-compliance-center">
      {/* Synthèse conformité */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h3 className="text-base font-semibold text-gray-800">Conformité documentaire</h3>
          <div className="flex flex-wrap gap-3">
            <KPI label="Documents requis" value={stats.requis} cls="text-gray-900" />
            <KPI label="Reçus" value={stats.recus} cls="text-emerald-600" />
            <KPI label="Manquants" value={stats.manquants} cls="text-red-600" />
            <KPI label="Validés" value={stats.valides} cls="text-blue-600" />
            <div className="flex-1 min-w-[140px] border rounded-xl px-4 py-3 bg-white">
              <p className="text-xs text-gray-400">Statut global</p>
              <p className={`text-lg font-bold mt-1 flex items-center gap-1.5 ${g.cls}`}><g.icon className="w-4 h-4" /> {g.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-emerald-600 w-28">{stats.progress}% complété</span>
            <Progress value={stats.progress} className="flex-1 h-2" />
            <span className="text-xs text-gray-400 whitespace-nowrap">{stats.recus} / {stats.requis} reçus</span>
          </div>

          {/* Zone upload rapide */}
          <div
            className="border-2 border-dashed border-gray-200 rounded-xl p-5 bg-gray-50/50"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onAddDoc('autre', e.dataTransfer.files); }}
            data-testid="quick-upload-zone"
          >
            <div className="text-center mb-4">
              <UploadCloud className="w-8 h-8 mx-auto text-emerald-500 mb-1" />
              <p className="text-sm font-medium text-gray-700">Glisser-déposer les documents ici</p>
              <p className="text-xs text-gray-400 mt-0.5">Ajoutez plusieurs fichiers à la fois.</p>
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAddDoc('autre')} data-testid="quick-add-doc">
                <UploadCloud className="w-4 h-4 mr-1.5" /> Ajouter un document
              </Button>
              <Button variant="outline" onClick={() => onAddDoc('autre')} data-testid="quick-import-multiple">
                <Download className="w-4 h-4 mr-1.5" /> Import multiple
              </Button>
              <Button variant="outline" onClick={() => onRequestField(stats.manquantsList)} data-testid="quick-request-field">
                <Send className="w-4 h-4 mr-1.5" /> Demander au terrain
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections A / B / C */}
      <div id="op-docs-sections" className="space-y-4">
        {DOC_SECTIONS.map((s) => (
          <Section key={s.key} section={s} docsByType={docsByType}
            count={sectionCount(s)} total={s.docs.length} {...rowProps} />
        ))}

        {/* Section D — Bons & signatures */}
        {hideSectionD ? null : sectionDContent ? (
          <div data-testid="doc-section-D">{sectionDContent}</div>
        ) : (
        <Card data-testid="doc-section-D">
          <Collapsible defaultOpen>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-semibold text-gray-800">D. Bons & signatures</span>
                </div>
                <Badge variant="outline" className="text-[11px] text-blue-700 border-blue-200 bg-blue-50">{dCount} / 2 créés</Badge>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t p-4">
                <LoadingNotesSection
                  entityId={operationId}
                  entityKind="operation"
                  operationWeight={operation?.estimatedWeight || operation?.weight}
                  onOpenNoteModal={(type) => setNoteModal({ open: true, type })}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </Card>
        )}
      </div>

      {/* Documents récents */}
      <Card id="op-docs-recent">
        <CardContent className="p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Documents récents</h3>
          {recentDocs.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <FolderOpen className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700">Aucun document ajouté</p>
              <p className="text-xs text-gray-400 mt-0.5 mb-4">Les documents ajoutés apparaîtront ici.</p>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onAddDoc('autre')} data-testid="empty-add-doc">
                <UploadCloud className="w-4 h-4 mr-1.5" /> Ajouter un document
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {recentDocs.map((d) => {
                const st = docStatus([d]);
                return (
                  <div key={d.id} className="flex items-center gap-3 py-2.5 text-sm flex-wrap" data-testid={`recent-${d.id}`}>
                    <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="flex-1 min-w-[160px] truncate text-gray-800">{d.fileName}</span>
                    <span className="w-28 text-xs text-gray-500 truncate">{d.documentType}</span>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_CONFIG[st]?.cls}`}>{STATUS_CONFIG[st]?.label}</Badge>
                    <span className="w-20 text-xs text-gray-500">{sourceLabel(d)}</span>
                    <span className="w-28 text-xs text-gray-500 truncate">{d.uploadedByName}</span>
                    <span className="w-20 text-xs text-gray-500">{fmtDate(d.uploadedAt)}</span>
                    <a href={`${BACKEND_URL}${d.filePath}`} download target="_blank" rel="noopener noreferrer" className="ml-auto">
                      <Button size="sm" variant="outline" className="h-7"><Download className="w-3.5 h-3.5" /></Button>
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {preview && (
        <PreviewDocumentModal
          open={!!preview}
          onOpenChange={(v) => !v && setPreview(null)}
          operationId={operationId}
          versions={preview.versions}
          label={preview.item?.label}
          canValidate={canValidate}
          onChanged={refetch}
        />
      )}

      {noteModal.open && (
        <LoadingNoteModal
          open={noteModal.open}
          onOpenChange={(v) => setNoteModal({ open: v, type: v ? noteModal.type : null })}
          operation={operation}
          type={noteModal.type}
          entityKind="operation"
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default DocumentsComplianceCenter;
